import * as React from 'react';
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  MoreHorizontalIcon,
} from 'lucide-react';
import { cn } from '../../Helpers/cn';

/**
 * Main pagination navigation component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ComponentProps<"nav">} props - All nav element props
 * @returns {React.JSX.Element} Pagination navigation element
 */
function Pagination({ className, ...props }) {
  return (
    <nav
      aria-label="pagination"
      data-slot="pagination"
      className={cn('mx-auto flex w-full justify-center', className)}
      {...props}
    />
  );
}

/**
 * Container for pagination items
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ComponentProps<"ul">} props - All ul element props
 * @returns {React.JSX.Element} Unordered list element
 */
function PaginationContent({ className, ...props }) {
  return (
    <ul
      data-slot="pagination-content"
      className={cn('flex flex-row items-center gap-2', className)}
      {...props}
    />
  );
}

/**
 * Individual pagination item wrapper
 * @param {React.ComponentProps<"li">} props - All li element props
 * @returns {React.JSX.Element} List item element
 */
function PaginationItem({ ...props }) {
  return (
    <li
      data-slot="pagination-item"
      className="inline-flex items-center justify-center h-9 min-w-9 overflow-clip rounded-full"
      {...props}
    />
  );
}

/**
 * Pagination link component
 * @param {Object} props - Component props
 * @param {boolean} [props.isActive] - Whether the link is for the current page
 * @param {React.ComponentProps<"a">} props - All anchor element props
 * @returns {React.JSX.Element} Anchor element styled as a button
 */
function PaginationLink({ className, isActive, ...props }) {
  return (
    <a
      aria-current={isActive ? 'page' : undefined}
      data-slot="pagination-link"
      data-active={isActive}
      className={cn(
        isActive ? 'button' : 'button-inverted border-transparent',
        'font-normal px-2 py-0 size-full inline-flex items-center justify-center',
        className
      )}
      {...props}
    />
  );
}

/**
 * Previous page button component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} props - All PaginationLink component props
 * @returns {React.JSX.Element} Previous page link component
 */
function PaginationPrevious({ className, ...props }) {
  return (
    <PaginationLink
      aria-label="Go to previous page"
      size="default"
      className={cn('gap-1 px-2.5 sm:pl-2.5', className)}
      {...props}
    >
      <ChevronLeftIcon />
      <span className="sr-only">Previous</span>
    </PaginationLink>
  );
}

/**
 * Next page button component
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {Object} props - All PaginationLink component props
 * @returns {React.JSX.Element} Next page link component
 */
function PaginationNext({ className, ...props }) {
  return (
    <PaginationLink
      aria-label="Go to next page"
      size="default"
      className={cn('gap-1 px-2.5 sm:pr-2.5', className)}
      {...props}
    >
      <span className="sr-only">Next</span>
      <ChevronRightIcon />
    </PaginationLink>
  );
}

/**
 * Ellipsis component for indicating more pages
 * @param {Object} props - Component props
 * @param {string} [props.className] - Additional CSS classes
 * @param {React.ComponentProps<"span">} props - All span element props
 * @returns {React.JSX.Element} Span element with ellipsis icon
 */
function PaginationEllipsis({ className, ...props }) {
  return (
    <span
      aria-hidden
      data-slot="pagination-ellipsis"
      className={cn('flex size-9 items-center justify-center', className)}
      {...props}
    >
      <MoreHorizontalIcon className="size-4" />
      <span className="sr-only">More pages</span>
    </span>
  );
}

export {
  Pagination,
  PaginationContent,
  PaginationLink,
  PaginationItem,
  PaginationPrevious,
  PaginationNext,
  PaginationEllipsis,
};
